import { useState, useEffect, useCallback, useMemo } from "react";
import useClientPaging from "../../components/admin/useClientPaging";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Link,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
  Build as BuildIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { toast } from "react-toastify";
import api from "../../api";

const BASE = "/admin/question-bank/school-practice-papers";

const FORMAT = {
  matches: { label: "Matches", bg: "#e8f5e9", color: "#1b5e20" },
  minor_gap: { label: "Minor gap", bg: "#fff3e0", color: "#9a4a00" },
  unverified: { label: "Unverified", bg: "#f3f4f6", color: "#374151" },
  mismatch: { label: "Mismatch", bg: "#ffebee", color: "#b71c1c" },
};

const ISSUE = {
  marks: { label: "Marks", bg: "#ffebee", color: "#b71c1c" },
  missing: { label: "Missing", bg: "#ffebee", color: "#b71c1c" },
  answer: { label: "Answer", bg: "#ffebee", color: "#b71c1c" },
  image: { label: "Image", bg: "#ffebee", color: "#b71c1c" },
  duplicate: { label: "Duplicate", bg: "#fff3e0", color: "#9a4a00" },
  format: { label: "Format", bg: "#fff3e0", color: "#9a4a00" },
  grok: { label: "Grok", bg: "#fff3e0", color: "#9a4a00" },
  qa: { label: "QA bot", bg: "#fff3e0", color: "#9a4a00" },
  wording: { label: "Wording", bg: "#f3f4f6", color: "#374151" },
  explanations: { label: "Explanations", bg: "#eef2ff", color: "#3B2A9F" },
  paper: { label: "Paper", bg: "#f3f4f6", color: "#374151" },
  signoff: { label: "Sign-off", bg: "#f3f4f6", color: "#374151" },
  // Exact check (practice-papers:audit-consortium) issue kinds
  figures: { label: "Figures", bg: "#ffebee", color: "#b71c1c" },
  source: { label: "Key differs", bg: "#ffebee", color: "#b71c1c" },
  expl: { label: "Explanation", bg: "#eef2ff", color: "#3B2A9F" },
};

const MOCK_STATUS = {
  final: { label: "Final", bg: "#e8f5e9", color: "#1b5e20" },
  failed: { label: "Check failed", bg: "#ffebee", color: "#b71c1c" },
  in_progress: { label: "Building", bg: "#f3f4f6", color: "#374151" },
  draft: { label: "Draft", bg: "#fff8e1", color: "#8a5a00" },
  not_built: { label: "Not built", bg: "#f3f4f6", color: "#6b7280" },
};

const MOCK_FORMAT = {
  matches: { label: "Matches", bg: "#e8f5e9", color: "#1b5e20" },
  "2027": { label: "2027 format", bg: "#ede9fe", color: "#3B2A9F" },
  "2027_provisional": { label: "2027 · provisional", bg: "#fff3e0", color: "#9a4a00" },
};

const ACTIONS = {
  refresh: { label: "Refresh", icon: <RefreshIcon /> },
  explain: {
    label: "Write missing explanations",
    icon: <AutoAwesomeIcon />,
    confirm: (s) =>
      `Grok will write an explanation for each of the ${(s?.explainable ?? 0) - (s?.explained ?? 0)} questions that have none. ` +
      "That is about one Grok call per question and uses AI credit. It runs in the background, 10 papers at a time, and the page refreshes when it finishes.",
  },
  qa: {
    label: "Run QA bot",
    icon: <AutoAwesomeIcon />,
    confirm: (s) =>
      `Grok checks every consortium practice question not yet clean (${s?.qa_unreviewed ?? 0} unchecked, ${s?.qa_held ?? 0} held): ` +
      "it solves each one, looks at the pictures, and the fix agent corrects wording, explanations and pictures. " +
      "Every fix is logged and can be undone; an answer change is only applied when a second check agrees. Uses AI credit; runs in the background.",
  },
  fix_marks: {
    label: "Apply mark fixes",
    icon: <BuildIcon />,
    confirm: (s) =>
      `This corrects ${s?.marks_to_fix ?? 0} marks from the papers' own mark schemes, adds ${s?.writing_missing ?? 0} missing writing tasks ` +
      `and removes ${s?.duplicates ?? 0} duplicate questions. No AI. Every paper's total then matches its mark scheme.`,
  },
};
ACTIONS.exact = {
  label: "Run exact check",
  icon: <BuildIcon />,
  confirm: () =>
    "Re-solves every consortium maths and coded reasoning answer from the question text, solves every non-verbal question from its figures, " +
    "and checks each explanation reaches its answer. No AI and no changes — it only reports. About a minute.",
};
const ENDPOINT = { refresh: "refresh", explain: "explain", fix_marks: "fix-marks", qa: "qa", exact: "exact" };

// Admin-only PDFs need the auth header, so fetch them as a blob and open that.
async function openPdf(path) {
  try {
    const res = await api.get(`${BASE}/${path}`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank", "noopener");
  } catch (e) {
    toast.error("Could not open the PDF.");
  }
}

function exactCell(e) {
  if (!e) return { text: "—", color: "#4b5563" };
  if (e.issues.length) {
    const kinds = e.issues.reduce((acc, i) => ({ ...acc, [i.kind]: (acc[i.kind] || 0) + 1 }), {});
    const words = { figures: "two-answer NVR", answer: "wrong key", source: "key differs", expl: "explanation" };
    const text = Object.entries(kinds).map(([k, n]) => `${n} ${words[k] || k}${n > 1 && k === "expl" ? "s" : ""}`).join(", ");
    return { text, color: kinds.answer || kinds.source || kinds.figures ? "#b71c1c" : "#9a4a00" };
  }
  const n = e.answers + e.figures;
  return n ? { text: `All ${n} checked`, color: "#1b5e20" } : { text: "—", color: "#4b5563" };
}

const KIND = { full_test: "Full test", subject: "Subject" };

const ok = "#1b5e20";
const bad = "#b71c1c";
const warn = "#9a4a00";
const muted = "#4b5563";

function pct(a, b) {
  return b ? Math.round((100 * a) / b) : 0;
}

function Tile({ label, value, of, note, color, progress }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography sx={{ fontSize: 26, fontWeight: 700, color }}>{value}</Typography>
        {of && <Typography sx={{ fontSize: 13, color: "#6b7280" }}>{of}</Typography>}
      </Box>
      {progress !== undefined && (
        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, bgcolor: "#eef0f4", "& .MuiLinearProgress-bar": { bgcolor: color } }} />
      )}
      {note && <Typography sx={{ fontSize: 12, color: muted }}>{note}</Typography>}
    </Paper>
  );
}

// 3 Nov 2026 meeting: rows for the school whose 11+ exam is soonest go to the top.
// next_exam_date comes from school_admission_cycles (SchoolExamDateService); no date sorts last.
const bySoonestExam = (a, b) => (a.next_exam_date || "9999-12-31").localeCompare(b.next_exam_date || "9999-12-31");

function ExamDate({ date }) {
  if (!date) return null;
  const days = Math.ceil((new Date(`${date}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  const color = days <= 14 ? bad : days <= 45 ? warn : muted;
  return (
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color, whiteSpace: "nowrap" }}>
      Exam {format(new Date(`${date}T00:00:00`), "d MMM yyyy")}{days <= 45 ? ` · ${days === 0 ? "today" : `in ${days} day${days === 1 ? "" : "s"}`}` : ""}
    </Typography>
  );
}

function Pill({ map, value }) {
  const c = map[value] || { label: value || "—", bg: "#f3f4f6", color: "#374151" };
  return <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 12 }} />;
}

function PaperRow({ p }) {
  const [open, setOpen] = useState(false);
  const marksOk = !p.marks_scheme || p.marks_live === p.marks_scheme;
  const exact = exactCell(p.exact);
  const issues = [...p.issues, ...(p.exact?.issues ?? []).map((i) => ({ kind: i.kind, qb_id: i.qb_id, text: `Exact check: ${i.text}` }))];
  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? 0 : undefined }, bgcolor: open ? "#fbfbfe" : undefined }}>
        <TableCell>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{p.name}</Typography>
          <Typography sx={{ fontSize: 12, color: muted }}>{p.schools.join(", ") || "—"}</Typography>
          <ExamDate date={p.next_exam_date} />
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>{p.subjects.join(" & ")}</TableCell>
        <TableCell sx={{ fontSize: 13, color: muted }}>{KIND[p.kind] || "School"}</TableCell>
        <TableCell><Pill map={FORMAT} value={p.format} /></TableCell>
        <TableCell>{p.questions}</TableCell>
        <TableCell sx={{ fontWeight: 500, color: marksOk ? ok : bad }}>
          {p.marks_live} / {p.marks_scheme || "?"}
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: p.answers_wrong ? bad : ok }}>{p.answers_wrong ? `${p.answers_wrong} wrong` : "OK"}</TableCell>
        <TableCell sx={{ fontWeight: 500, color: p.images_broken ? bad : ok }}>{p.images ? (p.images_broken ? `${p.images_broken} broken` : `${p.images} OK`) : "—"}</TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <Typography sx={{ fontSize: 13 }}>{p.explained} / {p.explainable}</Typography>
          <LinearProgress variant="determinate" value={pct(p.explained, p.explainable)} sx={{ height: 5, borderRadius: 3, bgcolor: "#eef0f4", "& .MuiLinearProgress-bar": { bgcolor: "#3B2A9F" } }} />
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: p.grok_to_check ? warn : muted }}>
          {p.grok_to_check ? `${p.grok_to_check} to check` : p.grok_wording ? `${p.grok_wording} wording` : "—"}
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: exact.color, bgcolor: "#faf8ff" }}>{p.source === "consortium" ? exact.text : "—"}</TableCell>
        <TableCell sx={{ fontWeight: 500, color: p.qa_held ? warn : p.qa_unreviewed ? muted : ok }}>
          {p.source === "consortium" || p.source === "independent"
            ? p.qa_held
              ? `${p.qa_held} held`
              : p.qa_unreviewed
              ? `${p.qa_unreviewed} to check`
              : `Clean${p.qa_fixed ? ` · ${p.qa_fixed} fixed` : ""}`
            : "—"}
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: p.signed_off ? ok : bad }}>{p.signed_off ? "Yes" : "No"}</TableCell>
        <TableCell align="right">
          <IconButton size="small" aria-label={open ? "Hide details" : "Show details"} onClick={() => setOpen((o) => !o)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={14} sx={{ py: 0, bgcolor: "#fbfbfe" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, py: 2 }}>
              <Box sx={{ flex: "2 1 520px", display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {issues.length ? `${issues.length} issue${issues.length === 1 ? "" : "s"} on this paper` : "No issues"}
                </Typography>
                {issues.map((i, n) => (
                  <Paper key={`${i.kind}-${i.qb_id ?? "x"}-${n}`} variant="outlined" sx={{ p: 1.25, display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Pill map={ISSUE} value={i.kind} />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>{i.text}</Typography>
                    {i.qb_id && (
                      <Link href={`/admin/question-bank/${i.qb_id}/edit`} target="_blank" rel="noopener" sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                        Edit question
                      </Link>
                    )}
                  </Paper>
                ))}
              </Box>
              <Box sx={{ flex: "1 1 240px", display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Fix</Typography>
                <Typography sx={{ fontSize: 13, color: "#374151" }}>
                  Marks, writing tasks, duplicates and paper fields: <b>Apply mark fixes</b>. Explanations: <b>Write missing explanations</b>.
                  Consortium papers: <b>Run QA bot</b>; anything it holds, edit the question. Answers and wording: edit the question.
                </Typography>
                {p.import_ids.map((id) => (
                  <Link key={id} href={`/admin/question-bank/import/${id}/review`} target="_blank" rel="noopener" sx={{ fontSize: 14 }}>
                    Open import review #{id}
                  </Link>
                ))}
                <Typography sx={{ fontSize: 12, color: muted }}>Paper #{p.paper_id} · {p.duration ? `${p.duration} min` : "no duration"}</Typography>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function MockRow({ m }) {
  const [open, setOpen] = useState(false);
  const c = m.counts;
  const cells = c ? [c.items, c.param_checked, c.nvr_checked, c.text_checked] : ["—", "—", "—", "—"];
  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? 0 : undefined }, bgcolor: open ? "#fbfbfe" : undefined }}>
        <TableCell>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{m.name}</Typography>
          <Typography sx={{ fontSize: 12, color: muted }}>{m.provider}</Typography>
          <ExamDate date={m.next_exam_date} />
        </TableCell>
        <TableCell><Pill map={MOCK_STATUS} value={m.status} /></TableCell>
        <TableCell><Pill map={MOCK_FORMAT} value={m.format} /></TableCell>
        <TableCell sx={{ fontSize: 12, color: "#374151" }}>{m.papers.length ? `${m.papers.length} paper${m.papers.length === 1 ? "" : "s"}` : "—"}</TableCell>
        {cells.map((v, i) => <TableCell key={i}>{v}</TableCell>)}
        <TableCell sx={{ fontSize: 12, fontStyle: "italic", color: "#374151" }}>{m.books.map((b) => b.title).join("; ") || "—"}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 13 }}>
          {m.pdfs.paper ? (
            <>
              <Link component="button" onClick={() => openPdf(`mock-pdf/${m.key}/paper`)}>Paper</Link>
              {m.pdfs.answers && <> · <Link component="button" onClick={() => openPdf(`mock-pdf/${m.key}/answers`)}>Answers</Link></>}
            </>
          ) : <Typography component="span" sx={{ color: "#6b7280" }}>—</Typography>}
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>
          {m.exams.length ? `${m.exams.length} · ${m.exams.every((e) => e.approved) ? "live" : "draft"}` : "—"}
        </TableCell>
        <TableCell align="right">
          <IconButton size="small" aria-label={open ? "Hide details" : "Show details"} onClick={() => setOpen((o) => !o)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={13} sx={{ py: 0, bgcolor: "#fbfbfe" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, py: 2 }}>
              <Box sx={{ flex: "2 1 520px", display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{m.name} — check report</Typography>
                {c ? (
                  <Paper variant="outlined" sx={{ p: 1.25, display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Pill map={MOCK_STATUS} value={m.errors.length ? "failed" : "final"} />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>
                      {c.items} questions · {c.param_checked} answers recomputed from the question text · {c.nvr_checked} NVR solved from the figures ·{" "}
                      {c.text_checked} English answered by a blind Grok check{c.written_checked ? ` · ${c.written_checked} mark schemes checked` : ""}
                      {c.writing ? ` · ${c.writing} writing task${c.writing === 1 ? "" : "s"}` : ""}
                    </Typography>
                  </Paper>
                ) : (
                  <Typography sx={{ fontSize: 13, color: muted }}>No check report yet.</Typography>
                )}
                {m.errors.map((e, n) => (
                  <Paper key={`e${n}`} variant="outlined" sx={{ p: 1.25, display: "flex", gap: 1.25 }}>
                    <Pill map={MOCK_STATUS} value="failed" />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>{e}</Typography>
                  </Paper>
                ))}
                {m.books.map((b, n) => (
                  <Paper key={`b${n}`} variant="outlined" sx={{ p: 1.25, display: "flex", gap: 1.25 }}>
                    <Chip size="small" label="Passage" sx={{ bgcolor: "#f3f4f6", color: "#374151", fontWeight: 700, fontSize: 12 }} />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>
                      {b.citation} — {b.words} words, public domain, Reference Book #{b.book_id}
                      {b.corrections.length ? ` · ${b.corrections.length} transcription fix${b.corrections.length === 1 ? "" : "es"}: ${b.corrections.map((f) => `"${f.from}" → "${f.to}"`).join(", ")}` : ""}
                    </Typography>
                  </Paper>
                ))}
                {m.warnings.map((w, n) => (
                  <Paper key={`w${n}`} variant="outlined" sx={{ p: 1.25, display: "flex", gap: 1.25 }}>
                    <Chip size="small" label="Note" sx={{ bgcolor: "#fff3e0", color: "#9a4a00", fontWeight: 700, fontSize: 12 }} />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>{w}</Typography>
                  </Paper>
                ))}
              </Box>
              <Box sx={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Papers</Typography>
                {m.papers.map((p) => (
                  <Typography key={p.title} sx={{ fontSize: 13 }}>{p.title} — {p.questions} q · {p.minutes} min</Typography>
                ))}
                {m.exams.length > 0 && <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 1 }}>Mock exams</Typography>}
                {m.exams.map((e) => (
                  <Typography key={e.id} sx={{ fontSize: 13 }}>
                    #{e.id} {e.name} — {e.approved ? `live, £${e.price.toFixed(2)}` : "draft"}
                  </Typography>
                ))}
                {m.checked_at && (
                  <Typography sx={{ fontSize: 12, color: muted, mt: 1 }}>Checked {format(new Date(m.checked_at), "d MMM yyyy, HH:mm")}</Typography>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function StructureTable({ consortiumKey, s }) {
  const [busy, setBusy] = useState(false);
  const cell = (c) => (c ? <Typography sx={{ fontSize: 13, color: c.differs ? bad : ok, fontWeight: c.differs ? 600 : 400 }}>{c.text}</Typography> : "—");
  const preview = async () => {
    setBusy(true);
    await openPdf(`practice-2027-pdf/${consortiumKey}`);
    setBusy(false);
  };
  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.75, bgcolor: "#fff", display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
        Section by section <Typography component="span" sx={{ fontSize: 13, color: muted }}>— official format vs our practice set vs our mock exam</Typography>
      </Typography>
      {s.official_note && (
        <Typography sx={{ fontSize: 12, color: muted }}>
          Official: {s.official_note}
          {s.official_source && <> (<Link href={s.official_source} target="_blank" rel="noopener">source</Link>)</>}
        </Typography>
      )}
      <TableContainer>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: "#f9fafb" }}>
            <TableRow>
              {["Paper / section", "Official", s.edition_live ? "Earlier set (hidden)" : "Practice set (today)", s.has_rebuild ? (s.edition_live ? "Practice set (2027, live)" : "Practice set (2027 rebuild)") : null, "Mock exam"].filter(Boolean).map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: "#374151" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {s.rows.map((r) => (
              <TableRow key={r.section}>
                <TableCell sx={{ fontWeight: 500, fontSize: 13 }}>{r.section}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.official}</TableCell>
                <TableCell>{cell(r.practice)}</TableCell>
                {s.has_rebuild && <TableCell>{cell(r.rebuilt)}</TableCell>}
                <TableCell>{cell(r.mock)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <Typography sx={{ fontSize: 12, color: muted, flex: 1 }}>
          Red = differs from the official format.{s.provisional ? " Timings are provisional: the provider has not published them." : ""} Question counts are not published by the providers; ours keep the current pacing.
        </Typography>
        {s.has_rebuild && (
          <>
            <Button variant="outlined" size="small" disabled={busy} onClick={preview}>{busy ? "Making PDF…" : "Preview 2027 practice set"}</Button>
            {s.edition_live ? (
              <Box sx={{ bgcolor: "#e8f5e9", color: ok, borderRadius: 2, px: 1.5, py: 0.5, fontSize: 13, fontWeight: 600 }}>
                2027 practice set live · earlier set hidden, kept for those who have it
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: muted }}>
                {s.edition
                  ? <>2027 set set up but not on the shop: run <code>practice-papers:import-consortium --phase=publish --only={consortiumKey}</code></>
                  : <>To publish it: set <code>'edition' =&gt; '2027'</code> for {consortiumKey} in config/consortium_practice_papers.php, then run publish, pdfs, publish.</>}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// A short label for a mock pill when a school row shows several (its own papers and a shared stage).
const mockLabel = (m) => (m.kind === "independent" ? m.name.replace(/ 11\+.*$/, "").replace(/ Common Pre-Test$/, "") : null);

function SchoolRow({ r, mocks, structure }) {
  const mock = mocks[0] ?? null;
  const [open, setOpen] = useState(false);
  const expandable = Boolean(structure);
  return (
    <>
      <TableRow hover sx={{ verticalAlign: "top", bgcolor: open ? "#fbfbfe" : undefined }}>
        <TableCell sx={{ fontWeight: 500 }}>
          {r.school}
          <Box><ExamDate date={r.next_exam_date} /></Box>
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>
          {r.real}
          {r.source && (
            <Box><Link href={r.source} target="_blank" rel="noopener" sx={{ fontSize: 12 }}>{r.source.replace(/^https?:\/\/(www\.)?/, "")}</Link></Box>
          )}
        </TableCell>
        <TableCell><Pill map={FORMAT} value={r.verdict} /></TableCell>
        <TableCell sx={{ fontSize: 13 }}>{r.note || "—"}</TableCell>
        <TableCell sx={{ fontSize: 13 }}>{r.papers_clean} / {r.paper_ids.length}</TableCell>
        <TableCell sx={{ fontSize: 13, color: r.pack_on_sale ? ok : muted }}>{r.pack_id ? (r.pack_on_sale ? "On sale" : "Off sale") : "No pack"}</TableCell>
        <TableCell sx={{ bgcolor: "#faf8ff" }}>
          {mocks.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
              {mocks.map((m) => (
                <Box key={m.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {mocks.length > 1 && mockLabel(m) && <Typography component="span" sx={{ fontSize: 12, color: muted }}>{mockLabel(m)}</Typography>}
                  <Pill map={MOCK_STATUS} value={m.status} />
                </Box>
              ))}
            </Box>
          ) : "—"}
        </TableCell>
        <TableCell align="right">
          {expandable && (
            <IconButton size="small" aria-label={open ? "Hide sections" : "Show sections"} onClick={() => setOpen((o) => !o)}>
              {open ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {expandable && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, bgcolor: "#fbfbfe", borderBottom: open ? undefined : 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2 }}><StructureTable consortiumKey={mock?.key || r.folder.replace("consortium:", "")} s={structure} /></Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function downloadCsv(papers) {
  const head = ["Paper", "Schools", "Subjects", "Kind", "Format", "Questions", "Marks live", "Marks scheme", "Answers wrong", "Images broken", "Explained", "Explainable", "Grok to check", "QA bot held", "QA bot to check", "Signed off", "Issues"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [head, ...papers.map((p) => [
    p.name, p.schools.join("; "), p.subjects.join(" & "), KIND[p.kind] || "School", p.format, p.questions, p.marks_live, p.marks_scheme, p.answers_wrong,
    p.images_broken, p.explained, p.explainable, p.grok_to_check, p.qa_held ?? "", p.qa_unreviewed ?? "", p.signed_off ? "yes" : "no", p.issues.map((i) => `[${i.kind}] ${i.text}`).join(" | "),
  ])].map((row) => row.map(esc).join(","));
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `school-practice-papers-${format(new Date(), "yyyy-MM-dd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SchoolPracticePapers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("papers");
  // 3 Nov 2026 meeting: independent schools as their own group, apart from grammar / consortium.
  const [group, setGroup] = useState("consortium");
  const [search, setSearch] = useState("");
  const [school, setSchool] = useState("");
  const [subject, setSubject] = useState("");
  const [region, setRegion] = useState("");
  const [consortium, setConsortium] = useState("");
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const [kind, setKind] = useState("");
  const [show, setShow] = useState("attention");
  const [confirm, setConfirm] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${BASE}/status`);
      setData(res.data?.data ?? null);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || "Could not load the status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // While a run is queued or going, poll so the progress bar moves and the result appears.
  const running = data?.running ?? [];
  useEffect(() => {
    if (!running.length) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running.length, load]);

  const start = async (kind) => {
    setStarting(true);
    try {
      const res = await api.post(`${BASE}/${ENDPOINT[kind]}`);
      toast.success(res.data?.message === "Already running" ? `${ACTIONS[kind].label} is already running.` : `${ACTIONS[kind].label} started.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || `Could not start ${ACTIONS[kind].label.toLowerCase()}.`);
    } finally {
      setStarting(false);
      setConfirm(null);
    }
  };

  const result = data?.result;
  const s = result?.summary;
  const cdata = data?.consortium;
  const mocks = useMemo(() => [...(cdata?.mocks ?? [])].sort(bySoonestExam), [cdata]);
  const mockByKey = useMemo(() => Object.fromEntries(mocks.map((m) => [m.key, m])), [mocks]);
  const mocksFinal = mocks.filter((m) => m.status === "final").length;
  const [mockShow, setMockShow] = useState("all");
  const exact = cdata?.exact;
  const papers = useMemo(() => [...(result?.papers ?? [])].sort(bySoonestExam), [result]);
  // A school row's date: the soonest of its papers' dates.
  const schoolRows = useMemo(() => {
    const byId = new Map(papers.map((p) => [p.paper_id, p.next_exam_date]));
    return (result?.schools ?? [])
      .map((r) => ({ ...r, next_exam_date: (r.paper_ids || []).map((id) => byId.get(id)).filter(Boolean).sort()[0] || null }))
      .sort(bySoonestExam);
  }, [papers, result]);
  const schoolNames = useMemo(() => [...new Set(papers.flatMap((p) => p.schools))].sort(), [papers]);
  const subjectNames = useMemo(() => [...new Set(papers.flatMap((p) => p.subjects))].sort(), [papers]);
  const regionNames = useMemo(() => [...new Set(papers.map((p) => p.region).filter(Boolean))].sort(), [papers]);
  const consortiumNames = useMemo(() => [...new Set(papers.map((p) => p.consortium).filter(Boolean))].sort(), [papers]);
  const inGroup = useCallback((isIndependent) => (group === "independent") === isIndependent, [group]);
  const groupPapers = useMemo(() => papers.filter((p) => inGroup(p.source !== "consortium")), [papers, inGroup]);
  const groupSchools = useMemo(() => schoolRows.filter((r) => inGroup(!String(r.folder || "").startsWith("consortium:"))), [schoolRows, inGroup]);
  const groupMocks = useMemo(() => mocks.filter((m) => inGroup(m.kind === "independent")), [mocks, inGroup]);
  const groupMocksFinal = groupMocks.filter((m) => m.status === "final").length;
  const groupCounts = {
    consortium: mocks.filter((m) => m.kind !== "independent").length,
    independent: mocks.filter((m) => m.kind === "independent").length,
  };
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groupPapers.filter((p) =>
      (show === "all" || (show === "clean" ? p.clean : !p.clean)) &&
      (!school || p.schools.includes(school)) &&
      (!region || p.region === region) &&
      (!consortium || p.consortium === consortium) &&
      (!category || (p.category || "").toLowerCase() === category) &&
      (!source || (p.source || "school") === source) &&
      (!kind || p.kind === kind) &&
      (!subject || p.subjects.includes(subject)) &&
      (!q || p.name.toLowerCase().includes(q) || p.schools.some((n) => n.toLowerCase().includes(q))));
  }, [groupPapers, search, school, subject, show, region, consortium, category, source, kind]);
  const { pageRows: pagedVisible, pager: visiblePager } = useClientPaging(visible);
  const isRunning = (kind) => running.some((j) => j.kind === kind);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh", display: "flex", flexDirection: "column", gap: 2.25 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 1.5 }}>
        <Box>
          <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
            Dashboard / Question Bank /{" "}
            <Box component="span" sx={{ color: "#3B2A9F", fontWeight: 600 }}>School Practice Papers</Box>
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>School &amp; Consortium Practice Papers — status</Typography>
          <Typography sx={{ fontSize: 14, color: muted }}>
            {s ? `${s.papers} practice papers in ${s.packs} school packs. ` : ""}
            {data?.checked_at
              ? `Last checked ${format(new Date(data.checked_at), "d MMM yyyy, HH:mm")}${data.checked_by ? ` by ${data.checked_by}` : ""}.`
              : "Not checked yet: press Refresh."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!papers.length} onClick={() => downloadCsv(visible)}>Download CSV</Button>
          <Button variant="outlined" startIcon={ACTIONS.explain.icon} disabled={!data?.can_act || starting || isRunning("explain")} onClick={() => setConfirm("explain")}>
            {ACTIONS.explain.label}
          </Button>
          <Button variant="outlined" startIcon={ACTIONS.qa.icon} disabled={!data?.can_act || starting || isRunning("qa")} onClick={() => setConfirm("qa")}>
            {ACTIONS.qa.label}
          </Button>
          <Button variant="outlined" startIcon={ACTIONS.fix_marks.icon} disabled={!data?.can_act || starting || isRunning("fix_marks")} onClick={() => setConfirm("fix_marks")}>
            {ACTIONS.fix_marks.label}
          </Button>
          <Button variant="outlined" startIcon={ACTIONS.exact.icon} disabled={!data?.can_act || starting || isRunning("exact")} onClick={() => setConfirm("exact")}>
            {ACTIONS.exact.label}
          </Button>
          <Button variant="contained" startIcon={<RefreshIcon />} disabled={starting || isRunning("refresh")} onClick={() => start("refresh")} sx={{ bgcolor: "#3B2A9F", "&:hover": { bgcolor: "#2a1d78" } }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {running.map((j) => (
        <Paper key={j.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 0.75 }}>
            {ACTIONS[j.kind]?.label ?? j.kind} — {j.status === "queued" ? "waiting for a worker" : `${j.progress}%`}
            {j.user ? ` · started by ${j.user}` : ""}
          </Typography>
          <LinearProgress variant={j.status === "queued" ? "indeterminate" : "determinate"} value={j.progress} sx={{ height: 6, borderRadius: 3 }} />
          {j.message && <Typography sx={{ fontSize: 12, color: muted, mt: 0.75 }}>{j.message}</Typography>}
        </Paper>
      ))}
      {(data?.last_actions ?? []).filter((j) => j.status === "failed").slice(0, 1).map((j) => (
        <Alert key={j.id} severity="error">{ACTIONS[j.kind]?.label} failed: {j.message}</Alert>
      ))}

      {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>}
      {!loading && error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && !result && !running.length && (
        <Alert severity="info">No check has run yet. Press <b>Refresh</b> to check every school practice paper (about a minute).</Alert>
      )}

      {s && (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 1.5 }}>
            <Tile label="Clean papers" value={s.papers_clean} of={`/ ${s.papers}`} progress={pct(s.papers_clean, s.papers)} color={ok} note="No issue of any kind" />
            <Tile label="Explanations" value={s.explained.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} of={`/ ${s.explainable.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`} progress={pct(s.explained, s.explainable)} color="#3B2A9F" note="Writing tasks excluded" />
            <Tile label="Marks to fix" value={s.marks_to_fix} of="questions" color={s.marks_to_fix ? bad : ok} note={`${s.papers_marks_wrong} papers off their mark scheme`} />
            <Tile label="Writing tasks missing" value={s.writing_missing} of="English papers" color={s.writing_missing ? bad : ok} note="Added by Apply mark fixes" />
            <Tile label="Duplicate questions" value={s.duplicates} color={s.duplicates ? warn : ok} note="Removed by Apply mark fixes" />
            <Tile label="Wrong bank answers" value={s.answers_wrong} color={s.answers_wrong ? bad : ok} note="Bank differs from the student paper" />
            <Tile label="Images" value={s.images_broken ? `${s.images_broken} broken` : "All load"} of={`${s.images} checked`} color={s.images_broken ? bad : ok} />
            <Tile label="Grok answers to check" value={s.grok_to_check} of={`of ${s.grok_total} flags`} color={s.grok_to_check ? warn : ok} note={`${s.grok_wording} wording notes`} />
            <Tile label="QA bot" value={s.qa_held ? `${s.qa_held} held` : (s.qa_unreviewed ? `${s.qa_unreviewed} to check` : "Clean")} of={`${(s.qa_clean ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} clean`} color={s.qa_held ? warn : s.qa_unreviewed ? muted : ok} note={`${s.qa_fixed ?? 0} fixed by the agent`} />
            <Tile label="Signed off" value={s.signed_off} of={`/ ${s.papers}`} progress={pct(s.signed_off, s.papers)} color={ok} />
            <Tile label="Packs on sale" value={s.packs_on_sale} of={`/ ${s.packs}`} color={muted} />
            {exact && (
              <Tile
                label="Exact check (consortium)"
                value={exact.issues ? `${exact.issues} issue${exact.issues === 1 ? "" : "s"}` : "Clean"}
                of={`${exact.wrong_keys} wrong keys`}
                color={exact.wrong_keys ? bad : exact.issues ? warn : ok}
                note={`${exact.answers.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} answers re-solved · ${exact.figures} figures solved${exact.by_kind?.figures ? ` · ${exact.by_kind.figures} two-answer NVR` : ""}${exact.by_kind?.expl ? ` · ${exact.by_kind.expl} explanations without working` : ""}`}
              />
            )}
            {mocks.length > 0 && (
              <Tile label="Mock exams" value={mocksFinal} of={`/ ${mocks.length} final`} progress={pct(mocksFinal, mocks.length)} color={ok}
                note={`${mocks.reduce((n, m) => n + m.exams.length, 0)} mock exams in the bank · ${mocks.filter((m) => m.pdfs.paper).length} with PDFs`} />
            )}
          </Box>

          <ToggleButtonGroup exclusive value={group} onChange={(_, v) => v && setGroup(v)} aria-label="School group" sx={{ alignSelf: "flex-start" }}>
            <ToggleButton value="consortium" sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}>Grammar &amp; consortium · {groupCounts.consortium}</ToggleButton>
            <ToggleButton value="independent" sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}>Independent schools · {groupCounts.independent}</ToggleButton>
          </ToggleButtonGroup>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tab value="papers" label={`Papers (${groupPapers.length})`} />
            <Tab value="schools" label={`Schools & formats (${groupSchools.length})`} />
            {groupMocks.length > 0 && <Tab value="mocks" label={`Mock exams (${groupMocks.length})`} />}
          </Tabs>

          {tab === "mocks" && (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
                <ToggleButtonGroup size="small" exclusive value={mockShow} onChange={(_, v) => v && setMockShow(v)} aria-label="Show mock exams">
                  <ToggleButton value="open">Not final · {groupMocks.length - groupMocksFinal}</ToggleButton>
                  <ToggleButton value="final">Final · {groupMocksFinal}</ToggleButton>
                  <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: 13, color: muted }}>
                  A mock is <b>final</b> when its check is clean: format, every answer recomputed or blind-checked, slight change, answer letters.
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
                <Table size="small" sx={{ minWidth: 1240 }}>
                  <TableHead sx={{ bgcolor: "#f9fafb" }}>
                    <TableRow>
                      {["Consortium / school", "Status", "Format", "Papers", "Qs", "Recomputed", "Figures", "Blind-checked", "Passage", "PDFs", "In the bank", ""].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#374151" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupMocks.filter((m) => mockShow === "all" || (mockShow === "final" ? m.status === "final" : m.status !== "final")).map((m) => <MockRow key={m.key} m={m} />)}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === "papers" && (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
                <TextField size="small" label="Search" placeholder="Paper or school" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: "1 1 260px", bgcolor: "#fff" }} />
                <FormControl size="small" sx={{ minWidth: 220, bgcolor: "#fff" }}>
                  <InputLabel id="spp-school">School</InputLabel>
                  <Select labelId="spp-school" label="School" value={school} onChange={(e) => setSchool(e.target.value)}>
                    <MenuItem value="">All schools</MenuItem>
                    {schoolNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170, bgcolor: "#fff" }}>
                  <InputLabel id="spp-subject">Subject</InputLabel>
                  <Select labelId="spp-subject" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}>
                    <MenuItem value="">All subjects</MenuItem>
                    {subjectNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, bgcolor: "#fff" }}>
                  <InputLabel id="spp-region">Region</InputLabel>
                  <Select labelId="spp-region" label="Region" value={region} onChange={(e) => setRegion(e.target.value)}>
                    <MenuItem value="">All regions</MenuItem>
                    {regionNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220, bgcolor: "#fff" }}>
                  <InputLabel id="spp-consortium">Consortium</InputLabel>
                  <Select labelId="spp-consortium" label="Consortium" value={consortium} onChange={(e) => setConsortium(e.target.value)}>
                    <MenuItem value="">All consortiums</MenuItem>
                    {consortiumNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170, bgcolor: "#fff" }}>
                  <InputLabel id="spp-category">Category</InputLabel>
                  <Select labelId="spp-category" label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
                    <MenuItem value="">Grammar and independent</MenuItem>
                    <MenuItem value="grammar">Grammar</MenuItem>
                    <MenuItem value="independent">Independent</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150, bgcolor: "#fff" }}>
                  <InputLabel id="spp-source">Source</InputLabel>
                  <Select labelId="spp-source" label="Source" value={source} onChange={(e) => setSource(e.target.value)}>
                    <MenuItem value="">All sources</MenuItem>
                    <MenuItem value="school">School packs</MenuItem>
                    <MenuItem value="consortium">Consortium papers</MenuItem>
                    <MenuItem value="independent">Independent papers</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150, bgcolor: "#fff" }}>
                  <InputLabel id="spp-kind">Kind</InputLabel>
                  <Select labelId="spp-kind" label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                    <MenuItem value="">All kinds</MenuItem>
                    <MenuItem value="full_test">Full test</MenuItem>
                    <MenuItem value="subject">Subject paper</MenuItem>
                  </Select>
                </FormControl>
                <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v) => v && setShow(v)} aria-label="Show papers">
                  <ToggleButton value="attention">Needs attention · {s.papers - s.papers_clean}</ToggleButton>
                  <ToggleButton value="clean">Clean · {s.papers_clean}</ToggleButton>
                  <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
                <Table size="small" sx={{ minWidth: 1180 }}>
                  <TableHead sx={{ bgcolor: "#f9fafb" }}>
                    <TableRow>
                      {["Paper", "Subject", "Kind", "Format", "Qs", "Marks (live / scheme)", "Answers", "Images", "Explanations", "Grok", "Exact check", "QA bot", "Signed off", ""].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#374151" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedVisible.map((p) => <PaperRow key={p.paper_id} p={p} />)}
                    {!visible.length && (
                      <TableRow><TableCell colSpan={14} sx={{ py: 4, textAlign: "center", color: muted }}>No papers match these filters.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {visiblePager}
              <Typography sx={{ fontSize: 12, color: muted }}>{visible.length} of {papers.length} papers match these filters</Typography>
            </>
          )}

          {tab === "schools" && (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
                {Object.entries(FORMAT).map(([k, c]) => (
                  <Box key={k} sx={{ bgcolor: c.bg, color: c.color, borderRadius: 2, px: 1.75, py: 1.25, fontSize: 14 }}>
                    <b>{s.formats?.[k] ?? 0}</b> {c.label.toLowerCase()}
                  </Box>
                ))}
                <Typography sx={{ fontSize: 13, color: muted }}>
                  Compared with each school's 2027-entry admissions page{result.formats_checked ? ` (checked ${result.formats_checked})` : ""}. Kept in config/school_mocks.php.
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
                <Table size="small" sx={{ minWidth: 1100 }}>
                  <TableHead sx={{ bgcolor: "#f9fafb" }}>
                    <TableRow>
                      {["School", "Real assessment (2027 entry)", "Format", "Gap / note", "Papers clean", "Pack", "Mock exam", ""].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#374151" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupSchools.filter((r) =>
                      (!region || r.region === region) &&
                      (!consortium || r.consortium === consortium) &&
                      (!category || (r.category || "").toLowerCase() === category)
                    ).map((r) => {
                      const key = r.folder?.startsWith("consortium:") ? r.folder.slice("consortium:".length) : null;
                      // Independent schools: their own mock exams and any shared stage they sit (ISEB, Habs QUEST, London Consortium).
                      const rowMocks = key ? [mockByKey[key]].filter(Boolean) : mocks.filter((m) => (m.folders || []).includes(r.folder));
                      return <SchoolRow key={r.folder} r={r} mocks={rowMocks} structure={key ? cdata?.structures?.[key] : null} />;
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      <Dialog open={Boolean(confirm)} onClose={() => !starting && setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm && ACTIONS[confirm].label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm && ACTIONS[confirm].confirm(s)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={starting}>Cancel</Button>
          <Button variant="contained" onClick={() => start(confirm)} disabled={starting} sx={{ bgcolor: "#3B2A9F", "&:hover": { bgcolor: "#2a1d78" } }}>
            {starting ? "Starting…" : "Start"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
